import { isBrowser } from '@utils';
import React, { useMemo } from 'react';
import Helmet from 'react-helmet';
import { embeddablePaths } from '@config/paths';
import { websiteConfig } from '@config/website';
import { useI18n } from '../context/I18nContext';
import { i18nConfig } from '@config/i18n';
import { getPathWithoutLocale } from '@utils/websiteUrlUtils';
import { jsonLdHelmetScript } from './SEO/jsonLd';

const organizationSchemaOrgBase = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": websiteConfig.schemaIdOrganization,
  "name": "Hyperhuman",
  "url": websiteConfig.url,
  "logo": {
    "@type": "ImageObject",
    "url": "https://hyperhuman.cdn.prismic.io/hyperhuman/396d8bb7-5aaf-493c-a9ca-1a2a9eb6bba5_logo.svg",
    "width": "300",
    "height": "54"
  },
  "image": {
    "@type": "ImageObject",
    "url": "https://hyperhuman.cdn.prismic.io/hyperhuman/396d8bb7-5aaf-493c-a9ca-1a2a9eb6bba5_logo.svg",
    "width": "300",
    "height": "54"
  },
  "foundingDate": "2020-12-02",
  "foundingLocation": {
    "@type": "Place",
    "name": "Bucharest, Romania",
    "sameAs": "https://en.wikipedia.org/wiki/Bucharest"
  },
  "description": websiteConfig.description,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": websiteConfig.url
  },
  "naics": "812990",
  "sameAs": [
    "https://www.linkedin.com/company/hyperhuman",
    "https://www.facebook.com/hyperhumancc",
    "https://www.instagram.com/hyperhumancc",
    "https://twitter.com/hyperhumancc",
    "https://www.tiktok.com/@hyperhumancc",
    "https://youtube.com/@hyperhumancc",
    "https://www.crunchbase.com/organization/hyperhuman",
    "https://team.hyperhuman.cc/",
    "https://mcp.hyperhuman.cc/"
  ],
  "founder": [
    {
      "@type": "Person",
      "name": "Bogdan Predusca",
      "url": "https://www.crunchbase.com/person/bogdan-predusca",
      "sameAs": "https://www.linkedin.com/in/bogdanpredusca/"
    }
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@hyperhuman.cc",
    "contactType": "Customer Support",
    "availableLanguage": ["English", "French", "German", "Spanish", "Italian", "Portuguese", "Hebrew", "Romanian", "Czech", "Finnish", "Dutch"]
  },
  "additionalType": [
    "https://en.wikipedia.org/wiki/Exercise",
    "https://en.wikipedia.org/wiki/Physical_fitness",
    "https://en.wikipedia.org/wiki/Well-being",
    "https://en.wikipedia.org/wiki/Nutrition",
    "https://en.wikipedia.org/wiki/Health_club",
    "https://en.wikipedia.org/wiki/Physical_therapy",
    "https://en.wikipedia.org/wiki/Fitness_app",
    "https://en.wikipedia.org/wiki/Computer_vision",
    "https://en.wikipedia.org/wiki/Video_production",
    "https://en.wikipedia.org/wiki/Application_programming_interface",
    "https://en.wikipedia.org/wiki/Artificial_intelligence"
  ],
  "alternateName": ["Hyperhuman Club", "Hyperhuman API", "Hyperhuman Studio"],
  "keywords": websiteConfig.schemaKeywords,
  "slogan": "Your product. Your audience. Our infrastructure.",
  "knowsAbout": [
    "AI fitness and wellness video content",
    "Content API for workouts and exercise data",
    "Branded client fitness applications",
    "AI-assisted video editing for exercise media"
  ]
};

export const Head = function Head() {
  const { locale } = useI18n();
  const htmlLang = useMemo(() => {
    const config = locale ? i18nConfig[locale as keyof typeof i18nConfig] : null;
    return config?.ogLang?.split('_')[0] ?? 'en';
  }, [locale]);
  const organizationSchemaOrgJSONLD = useMemo(
    () => ({
      ...organizationSchemaOrgBase,
      inLanguage: htmlLang,
    }),
    [htmlLang]
  );
  const htmlDir = useMemo(() => {
    const config = locale ? i18nConfig[locale as keyof typeof i18nConfig] : null;
    return config?.dir ?? 'ltr';
  }, [locale]);

  const thirdpartyEnabled = useMemo(() => {
    if (isBrowser()) {
      const basePath = getPathWithoutLocale(window.location.pathname);
      const isEmbeddable = embeddablePaths.includes(basePath);
      if (isEmbeddable) {
        return false;
      }
      return true;
    }
    return false;
  }, []);

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: htmlLang,
          dir: htmlDir,
        }}
        script={[jsonLdHelmetScript('ld-organization', organizationSchemaOrgJSONLD)]}
      >
        {/* Favicon configuration for all browsers and devices */}
        <link
          rel='icon'
          type='image/x-icon'
          href='/favicon.ico'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='32x32'
          href={`https://images.prismic.io/hyperhuman/f4b5a888-1aca-4611-8e04-2efbf2e4ca04_favicon.png?auto=compress,format&w=32&h=32`}
        />
        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href={`https://images.prismic.io/hyperhuman/f4b5a888-1aca-4611-8e04-2efbf2e4ca04_favicon.png?auto=compress,format&w=16&h=16`}
        />
        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href={`https://images.prismic.io/hyperhuman/f4b5a888-1aca-4611-8e04-2efbf2e4ca04_favicon.png?auto=compress,format&w=180&h=180`}
        />
        <link
          rel='manifest'
          href='/site.webmanifest'
        />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1, viewport-fit=cover'
        />
        <meta property='fb:app_id' content='529382751414370' />
      </Helmet>
      {thirdpartyEnabled ? <ThirdPartyHelmet /> : <></>}
    </>
  );
};

const ThirdPartyHelmet = React.memo(function ThirdPartyHelmet() {
  return (
    <Helmet>
      <script async={true}>
        {` (function(c,l,a,r,i,t,y){
c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "dx9tmkg90p");`}
      </script>
      <script async={true}>
        {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;

// Set default consent (only needs to be done once)
if (!localStorage.getItem('ga_consent_defaults_set')) {
  gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted'
  });
  localStorage.setItem('ga_consent_defaults_set', 'true');
}`}
      </script>

      <script async={true}>
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-5ZKXB3H');
`}
      </script>
      {/* Meta Pixel (130546408847547) managed through GTM container GTM-5ZKXB3H above */}
      {/* Intercom Messenger */}
      <script async={true}>
        {`
window.intercomSettings = {
  api_base: "https://api-iam.intercom.io",
  app_id: "o0w07ri8",
};
`}
      </script>
      <script async={true}>
        {`
(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/o0w07ri8';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
`}
      </script>
      <script async={true}>
        {`
_linkedin_partner_id = "3656476"; window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []; window._linkedin_data_partner_ids.push(_linkedin_partner_id); (function(l) { if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])}; window.lintrk.q=[]} var s = document.getElementsByTagName("script")[0]; var b = document.createElement("script"); b.type = "text/javascript";b.async = true; b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js"; s.parentNode.insertBefore(b, s);})(window.lintrk);
`}
      </script>
    </Helmet>
  );
});
